import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

const DISPLAY_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Flips the sign of a numeric-text field value. Lets users enter negative
 * temperatures on keypads with no minus key (notably the iOS decimal pad).
 * Empty ⇄ "-", "18.5" ⇄ "-18.5". The result always satisfies the temp-input
 * guard (`-?\d*(?:\.\d*)?` plus the "" and "-" literals), so callers can pass
 * it straight to the same onChange path the typed input uses.
 */
export function toggleTempSign(v: string): string {
  return v.startsWith('-') ? v.slice(1) : `-${v}`
}

/** Formats an ISO date string as "05 Nov 2025". Returns "—" for empty/invalid input. */
export function formatShortDate(dateStr: string): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** yyyy-mm-dd for a Date, in local time. */
export function toISODate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Parse a yyyy-mm-dd string to a local-midnight Date. */
export function fromISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1)
}

/** Human date like "04 Mar 2025". */
export function formatDisplayDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, '0')
  return `${day} ${DISPLAY_MONTHS[date.getMonth()]} ${date.getFullYear()}`
}

/** Human date like "Mon, 25 Aug 2026" (used on cart group date pills). */
export function formatLongDate(date: Date): string {
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]
  return `${weekday}, ${formatDisplayDate(date)}`
}
